import React, { memo, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { areEqual } from 'Utils/equalityChecks';

import { userFeatureFlagsSelector } from 'Containers/User/selector';
import { firstCompanyIdSelector } from 'Containers/Projects/selectors';
import { setFirstCompanyId, switchCompany } from 'Containers/User/actions';

import { SideBar } from 'Components/SideBar';
import { navItems } from 'Utils/navItems';
import { useUser } from 'Context/User';
import { UserModel } from 'Containers/User/Models/UserModel';

interface Props {
  sideBarDesktop: boolean;
  sideBarMobile: boolean;
  toggleSideBar: () => void;
  pathname: string;
}

function SideBarContainer({ sideBarDesktop, sideBarMobile, toggleSideBar, pathname }: Props) {
  const dispatch = useDispatch();
  const user = useUser();

  const { companies = [] } = user;
  const { eSignature, timeTracking, rocketScope } = useSelector(userFeatureFlagsSelector, areEqual);
  const { isCompanyAdmin }: UserModel = useUser();
  const firstCompanyId = useSelector(firstCompanyIdSelector, areEqual);

  const [availableItems, setAvailableItems] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [switchingCompany, setSwitchingCompany] = useState(false);

  useEffect(() => {
    const fallbackCompanyId = firstCompanyId || companies?.[0]?.id;

    if (fallbackCompanyId && fallbackCompanyId !== selectedCompanyId) {
      setSelectedCompanyId(fallbackCompanyId);
    }

    if (!firstCompanyId && fallbackCompanyId) {
      dispatch(setFirstCompanyId(fallbackCompanyId));
    }
  }, [companies, dispatch, firstCompanyId, selectedCompanyId]);

  useLayoutEffect(() => {
    let filtered = navItems;

    if (!eSignature) {
      filtered = filtered.filter((navItem) => navItem.title !== 'Forms');
    }

    if (!timeTracking) {
      filtered = filtered.filter((navItem) => navItem.title !== 'Timesheet');
    }

    if (!rocketScope) {
      filtered = filtered.filter((navItem) => navItem.title !== 'Rocket Scope');
    }

    if (!isCompanyAdmin) {
      filtered = filtered.filter((navItem) => !navItem.adminOnly);
    }

    setAvailableItems(filtered);
  }, [eSignature, isCompanyAdmin, rocketScope, timeTracking]);

  const handleCompanyChange = useCallback(
    async (companyId: number) => {
      if (!companyId || companyId === selectedCompanyId || switchingCompany) return;

      setSwitchingCompany(true);
      const previousCompanyId = selectedCompanyId;
      setSelectedCompanyId(companyId);

      const switched = await dispatch(switchCompany(companyId));

      if (!switched && previousCompanyId) {
        setSelectedCompanyId(previousCompanyId);
      }

      setSwitchingCompany(false);
    },
    [dispatch, selectedCompanyId, switchingCompany]
  );

  return (
    <SideBar
      sideBarDesktop={sideBarDesktop}
      sideBarMobile={sideBarMobile}
      toggleSideBar={toggleSideBar}
      navItems={availableItems}
      pathname={pathname}
      companies={companies}
      selectedCompanyId={selectedCompanyId}
      onCompanyChange={handleCompanyChange}
      switchingCompany={switchingCompany}
    />
  );
}
const SideBarContainerMemo = memo(SideBarContainer, areEqual);

export { SideBarContainerMemo as SideBarContainer };
